/**
 * Purpose:
 *   Centralize the current account navigation rules in one KISS-friendly place.
 *
 * Notes:
 *   Owns shared account route builders and breadcrumb composition.
 */

import type { IBreadcrumbItem } from '@/components/ui/breadcrumb';
import type { TFunction } from 'i18next';
import { buildAccountTabHref } from './accountRoutes';

export type TAccountProfileUpsertMode = 'create' | 'edit';
export type TAddressUpsertMode = 'create' | 'edit';

export const ACCOUNT_OVERVIEW_ACTIVE_PATH_PATTERNS = [
  '^/account$',
  '^/account/overview(?:/.*)?$',
  '^/account/acc_[A-Z0-9]+(?:/overview(?:/.*)?)?$',
];

export function buildAuthenticatedAccountOverviewHref(
) {
  return '/account/overview';
}

export function buildAccountAccountsTabHref() {
  return buildAccountTabHref(undefined, 'accounts');
}

export function buildAccountProfileOverviewHref(
  accountId: string
) {
  return `/account/overview/${accountId}`;
}

export function buildAccountProfileCreateHref(
  parentAccountId: string,
  isOwnAccount = false
) {
  return isOwnAccount
    ? '/account/overview/new'
    : `/account/overview/${parentAccountId}/new`;
}

export function buildAccountProfileEditHref(
  accountId: string,
  isOwnAccount: boolean
) {
  return isOwnAccount
    ? '/account/overview/edit'
    : `/account/overview/${accountId}/edit`;
}

export function buildUserUpsertUsersTabHref(
  accountId: string,
  isOwnAccount: boolean
): string {
  return buildAccountTabHref(isOwnAccount ? undefined : accountId, 'users');
}

export function buildUserCreateHref(
  accountId: string,
  isOwnAccount: boolean
): string {
  return isOwnAccount
    ? '/account/overview/users/new'
    : `/account/overview/${accountId}/users/new`;
}

export function buildUserEditHref(
  accountId: string,
  userId: string,
  isOwnAccount: boolean
): string {
  return isOwnAccount
    ? `/account/overview/users/${userId}/edit`
    : `/account/overview/${accountId}/users/${userId}/edit`;
}

export function buildAddressUpsertAddressesTabHref(
  accountId: string,
  isOwnAccount: boolean
): string {
  return buildAccountTabHref(isOwnAccount ? undefined : accountId, 'addresses');
}

export function buildAddressCreateHref(
  accountId: string,
  isOwnAccount: boolean
): string {
  return isOwnAccount
    ? '/account/overview/addresses/new'
    : `/account/overview/${accountId}/addresses/new`;
}

export function buildAddressEditHref(
  accountId: string,
  addressId: string,
  isOwnAccount: boolean
): string {
  return isOwnAccount
    ? `/account/overview/addresses/${addressId}/edit`
    : `/account/overview/${accountId}/addresses/${addressId}/edit`;
}

export function buildAccountProfileUpsertBreadcrumbItems(args: {
  accountId: string;
  accountLabel: string | null;
  isOwnAccount: boolean;
  mode: TAccountProfileUpsertMode;
  tCommon: TFunction<'common'>;
  tAccount: TFunction<'account'>;
}): IBreadcrumbItem[] {
  return [
    ...buildAccountScopedBreadcrumbBase(args),
    {
      label:
        args.mode === 'create'
          ? args.tAccount('account_form.breadcrumb_create')
          : args.tAccount('account_form.breadcrumb_edit'),
      isCurrent: true,
    },
  ];
}

export function buildUserUpsertBreadcrumbItems(args: {
  accountId: string;
  accountLabel: string | null;
  isOwnAccount: boolean;
  tCommon: TFunction<'common'>;
  tAccount: TFunction<'account'>;
  mode: 'create' | 'edit';
}): IBreadcrumbItem[] {
  return [
    ...buildAccountScopedBreadcrumbBase(args),
    {
      label:
        args.mode === 'create'
          ? args.tAccount('users.breadcrumb_create')
          : args.tAccount('users.breadcrumb_edit'),
      isCurrent: true,
    },
  ];
}

export function buildAddressUpsertBreadcrumbItems(args: {
  accountId: string;
  accountLabel: string | null;
  isOwnAccount: boolean;
  tCommon: TFunction<'common'>;
  tAccount: TFunction<'account'>;
  mode: TAddressUpsertMode;
}): IBreadcrumbItem[] {
  return [
    ...buildAccountScopedBreadcrumbBase(args),
    {
      label:
        args.mode === 'create'
          ? args.tAccount('addresses.breadcrumb_create')
          : args.tAccount('addresses.breadcrumb_edit'),
      isCurrent: true,
    },
  ];
}

export function buildAccountScopedBreadcrumbBase(args: {
  accountId: string;
  accountLabel: string | null;
  isOwnAccount: boolean;
  tCommon: TFunction<'common'>;
  tAccount: TFunction<'account'>;
}): IBreadcrumbItem[] {
  const shouldShowAccountLabel = Boolean(args.accountLabel) && !args.isOwnAccount;
  const ownOverviewHref = buildAuthenticatedAccountOverviewHref();
  const accountOverviewHref = buildAccountProfileOverviewHref(args.accountId);
  return [
    { label: args.tCommon('breadcrumb.home'), href: '/' },
    {
      label: args.tCommon('menu.account_overview'),
      href: ownOverviewHref,
    },
    ...(shouldShowAccountLabel
      ? [
        {
          label: args.accountLabel as string,
          href: args.isOwnAccount ? ownOverviewHref : accountOverviewHref,
        },
      ]
      : []),
  ];
}
